import React, { useEffect, useState } from 'react';
import { File, Filter, X, Eye, ListFilter } from 'lucide-react';
import { Link, useSearchParams } from 'react-router-dom';
import { documentsApi, DocumentsByStructure, Document as DocType } from '../api/documents';
import { categoriesApi, Category } from '../api/categories';
import { authApi } from '../api/auth';
import toast from 'react-hot-toast';
import { format } from 'date-fns';

/** Keep only files uploaded by the given user; remove empty categories/groups. */
function filterStructureToUploader(
  data: DocumentsByStructure[],
  uploadedById: number
): DocumentsByStructure[] {
  return data
    .map((g) => ({
      ...g,
      categories: g.categories
        .map((c) => ({
          ...c,
          files: c.files.filter((f) => f.uploaded_by?.id === uploadedById),
        }))
        .filter((c) => c.files.length > 0),
    }))
    .filter((g) => g.categories.length > 0);
}

export const DocumentsPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [structure, setStructure] = useState<DocumentsByStructure[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentUserId, setCurrentUserId] = useState<number | null>(null);
  const [showFilters, setShowFilters] = useState(false);
  const [showAllDocuments, setShowAllDocuments] = useState(false);
  const [dateFrom, setDateFrom] = useState<string>('');
  const [dateTo, setDateTo] = useState<string>('');
  const [uploadedByFilter, setUploadedByFilter] = useState<string>('');
  const [categoryFilter, setCategoryFilter] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [allUsers, setAllUsers] = useState<Array<{ id: number; username: string; email: string; first_name?: string }>>([]);
  const [allCategories, setAllCategories] = useState<Category[]>([]);

  // Restore filter state from URL (so back button returns to same state)
  useEffect(() => {
    setShowAllDocuments(searchParams.get('all') === '1');
    setDateFrom(searchParams.get('dateFrom') ?? '');
    setDateTo(searchParams.get('dateTo') ?? '');
    setUploadedByFilter(searchParams.get('uploadedBy') ?? '');
    setCategoryFilter(searchParams.get('category') ?? '');
    setStatusFilter(searchParams.get('status') ?? '');
  }, [searchParams]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const user = await authApi.getCurrentUser();
        if (!cancelled && user?.id) setCurrentUserId(user.id);
      } catch {
        const stored = localStorage.getItem('user');
        if (stored) {
          try {
            const parsed = JSON.parse(stored);
            if (!cancelled && parsed?.id) setCurrentUserId(parsed.id);
          } catch {}
        }
      }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    fetchDocuments();
    fetchUsers();
    fetchCategories();
  }, [dateFrom, dateTo, uploadedByFilter, categoryFilter, statusFilter, showAllDocuments, currentUserId]);

  // Persist filter state to URL so back button returns to same state
  useEffect(() => {
    const params: Record<string, string> = {};
    if (showAllDocuments) params.all = '1';
    if (dateFrom) params.dateFrom = dateFrom;
    if (dateTo) params.dateTo = dateTo;
    if (uploadedByFilter) params.uploadedBy = uploadedByFilter;
    if (categoryFilter) params.category = categoryFilter;
    if (statusFilter) params.status = statusFilter;
    const current = Object.fromEntries(searchParams.entries());
    const same = Object.keys({ ...current, ...params }).every((k) => String(current[k] ?? '') === String(params[k] ?? ''));
    if (!same) setSearchParams(params, { replace: true });
  }, [showAllDocuments, dateFrom, dateTo, uploadedByFilter, categoryFilter, statusFilter, searchParams]);

  const fetchUsers = async () => {
    try {
      const users = await documentsApi.getAllUsers();
      setAllUsers(users);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await categoriesApi.getAll(false);
      setAllCategories(response.results || response);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchDocuments = async () => {
    try {
      setLoading(true);
      const data = await documentsApi.getByStructure(
        !showAllDocuments, // my documents = controls where I am assignee
        dateFrom || undefined,
        dateTo || undefined,
        categoryFilter ? parseInt(categoryFilter) : undefined,
        uploadedByFilter ? parseInt(uploadedByFilter) : undefined,
        statusFilter || undefined
      );
      let result = Array.isArray(data) ? data : [];
      // In My Documents mode, only show files uploaded by the current user (safety net if backend filter misses)
      if (!showAllDocuments && currentUserId != null) {
        result = filterStructureToUploader(result, currentUserId);
      }
      setStructure(result);
    } catch (error: any) {
      console.error('Error fetching documents:', error);
      const status = error.response?.status;
      const msg = error.response?.data?.detail || error.response?.data?.error || error.message;
      const isNetworkError = !error.response;
      if (status === 401) {
        toast.error('Please log in again.');
      } else if (status === 403) {
        toast.error('You don’t have access to view these documents.');
      } else if (status === 404) {
        toast.error('Documents API not found. Is the backend running with the latest code?');
      } else if (isNetworkError) {
        const base = process.env.REACT_APP_API_URL || 'backend';
        toast.error(`Cannot reach the server at ${base}. Is the backend running?`);
      } else {
        const suffix = status ? ` (${status})` : '';
        toast.error(msg ? `Failed to load documents: ${msg}${suffix}` : `Failed to load documents${suffix}`);
      }
    } finally {
      setLoading(false);
    }
  };

  const clearFilters = () => {
    setDateFrom('');
    setDateTo('');
    setUploadedByFilter('');
    setCategoryFilter('');
    setStatusFilter('');
    setSearchParams({}, { replace: true });
  };

  const hasActiveFilters = dateFrom || dateTo || uploadedByFilter || categoryFilter || statusFilter;

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(2) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
  };

  const formatDate = (dateString: string): string => {
    try {
      return format(new Date(dateString), 'MMM d, yyyy');
    } catch {
      return dateString;
    }
  };

  const getStatusLabel = (status: string | undefined): string => {
    if (!status) return '—';
    const labels: Record<string, string> = {
      PENDING: 'Pending',
      SUBMITTED: 'Submitted',
      UNDER_REVIEW: 'Under review',
      APPROVED: 'Approved',
      REJECTED: 'Rejected',
    };
    return labels[status] || status;
  };

  const getStatusBadgeClass = (status: string | undefined): string => {
    if (!status) return 'bg-gray-100 text-gray-700';
    switch (status) {
      case 'APPROVED': return 'bg-green-100 text-green-800';
      case 'REJECTED': return 'bg-red-100 text-red-800';
      case 'SUBMITTED': return 'bg-blue-100 text-blue-800';
      case 'UNDER_REVIEW': return 'bg-amber-100 text-amber-800';
      case 'PENDING': default: return 'bg-gray-100 text-gray-700';
    }
  };

  const allFiles = structure.flatMap((g) => g.categories.flatMap((c) => c.files));
  const totalFiles = allFiles.length;

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="text-gray-500">Loading documents...</div>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Documents</h1>
          <p className="text-sm text-gray-500 mt-1">
            {totalFiles} {totalFiles === 1 ? 'document' : 'documents'} found
            {!showAllDocuments && ' (my controls)'}
          </p>
        </div>
        <div className="flex items-center gap-4">
          <div className="flex rounded-lg border border-gray-300 overflow-hidden">
            <button
              onClick={() => setShowAllDocuments(false)}
              className={`flex items-center gap-2 px-4 py-2 font-medium transition-all ${
                !showAllDocuments
                  ? 'bg-blue-500 text-white'
                  : 'bg-white text-gray-700 hover:bg-gray-50'
              }`}
            >
              <ListFilter size={18} />
              <span className="text-sm">My Documents</span>
            </button>
            <button
              onClick={() => setShowAllDocuments(true)}
              className={`flex items-center gap-2 px-4 py-2 font-medium transition-all ${
                showAllDocuments
                  ? 'bg-blue-500 text-white'
                  : 'bg-white text-gray-700 hover:bg-gray-50'
              }`}
            >
              <span className="text-sm">All Documents</span>
            </button>
          </div>
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`flex items-center gap-2 px-4 py-2 border rounded-lg transition-colors ${
              showFilters || hasActiveFilters
                ? 'bg-blue-500 text-white border-blue-500'
                : 'border-gray-300 hover:bg-gray-50'
            }`}
          >
            <Filter size={20} />
            <span>Filters</span>
            {hasActiveFilters && (
              <span className="bg-white text-blue-500 text-xs rounded-full px-2 py-0.5">
                {[dateFrom, dateTo, uploadedByFilter, categoryFilter, statusFilter].filter(Boolean).length}
              </span>
            )}
          </button>
          {hasActiveFilters && (
            <button
              onClick={clearFilters}
              className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:text-gray-900"
            >
              <X size={20} />
              Clear Filters
            </button>
          )}
        </div>
      </div>

      {/* Filters Panel */}
      {showFilters && (
        <div className="bg-white rounded-lg shadow p-6 mb-6 border border-gray-200">
          <h2 className="text-lg font-semibold mb-4">Filter Documents</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Date From</label>
              <input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Date To</label>
              <input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Uploaded By</label>
              <select
                value={uploadedByFilter}
                onChange={(e) => setUploadedByFilter(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">All Users</option>
                {allUsers.map((user) => (
                  <option key={user.id} value={user.id.toString()}>
                    {user.first_name || user.username} ({user.email})
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Control</label>
              <select
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">All Controls</option>
                {allCategories.map((category) => (
                  <option key={category.id} value={category.id.toString()}>
                    {category.name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">All statuses</option>
                <option value="PENDING">Pending</option>
                <option value="SUBMITTED">Submitted</option>
                <option value="UNDER_REVIEW">Under review</option>
                <option value="APPROVED">Approved</option>
                <option value="REJECTED">Rejected</option>
              </select>
            </div>
          </div>
        </div>
      )}

      {/* Document list: no categories, control name as link below each document */}
      {allFiles.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-12 text-center">
          <File className="mx-auto text-gray-400 mb-4" size={48} />
          <p className="text-gray-500 text-lg">No documents found</p>
          {!showAllDocuments && (
            <p className="text-gray-400 text-sm mt-2">You may have no documents in controls assigned to you.</p>
          )}
          {hasActiveFilters && (
            <p className="text-gray-400 text-sm mt-2">Try adjusting your filters or switch to All Documents.</p>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {allFiles.map((file: DocType) => (
            <div
              key={file.id}
              className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
            >
              <div className="flex items-start justify-between gap-2 mb-2">
                <div className="flex items-center gap-2 flex-1 min-w-0">
                  <File className="text-blue-500 flex-shrink-0" size={20} />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-gray-900 truncate" title={file.filename}>
                      {file.filename}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      {file.uploaded_by
                        ? (file.uploaded_by as { first_name?: string }).first_name || file.uploaded_by.username
                        : '—'} • {formatDate(file.uploaded_at)}
                    </p>
                    <p className="text-xs mt-1">
                      <Link
                        to={`/categories/${file.category_id}`}
                        className="text-blue-600 hover:text-blue-700 hover:underline"
                      >
                        {file.category_name}
                      </Link>
                    </p>
                  </div>
                </div>
                <a
                  href={file.file_url || file.google_drive_file_url || '#'}
                  target="_blank"
                  rel="noopener noreferrer"
                  title="View document"
                  className="flex-shrink-0 p-1 text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded"
                >
                  <Eye size={20} />
                </a>
              </div>
              <div className="flex items-center justify-between mt-3 pt-3 border-t border-gray-100">
                <div className="text-xs text-gray-500">
                  {formatFileSize(file.file_size)} • {file.mime_type.split('/')[1]?.toUpperCase() || 'FILE'}
                </div>
                {file.status && (
                  <span className={`px-2 py-0.5 rounded text-xs font-medium ${getStatusBadgeClass(file.status)}`}>
                    {getStatusLabel(file.status)}
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
